import { useMemo } from 'react';
import { useTier } from '../context/TierContext';
import { useNegotiation } from '../context/NegotiationContext';
import { useConfirmedRecommendationCount } from './useConfirmedRecommendationCount';

/**
 * useNavLinks - Unified navigation links hook for all logged-in screens
 *
 * Provides consistent navigation links across all screens with tier-based access control.
 * Automatically shows/hides "My Reports" based on user tier and recommendation count.
 *
 * @param {Object} params
 * @param {Function} params.logout - Logout function from AuthContext
 * @param {Object} params.session - User session from AuthContext
 * @param {string} [params.mode] - Optional mode ('shared' for public views, 'live' for authenticated)
 * @returns {Array} Navigation links array
 *
 * @example
 * const navLinks = useNavLinks({ logout, session });
 * // Returns: [{ title: 'My Cars', target: '/dashboard' }, ...]
 */
export const useNavLinks = ({ logout, session, mode = 'live' }) => {
  const { hasProAccess } = useTier();
  const { unreadCount, pendingDraftCount } = useNegotiation();
  const { count: confirmedRecommendationCount } = useConfirmedRecommendationCount(session);
  const negotiationBadge = (unreadCount || 0) + (pendingDraftCount || 0);

  const navLinks = useMemo(() => {
    // Shared mode (public view) - minimal navigation
    if (mode === 'shared') {
      return [{ title: 'Home', target: '/' }];
    }

    // Redesign mode - fixed navigation (no logout, no tier checks)
    if (mode === 'redesign') {
      const links = [
        { title: 'Home', target: '/home' },
        { title: 'Favorites', target: '/favorites' },
        { title: 'Cars', target: '/cars' },
      ];
      if (hasProAccess) {
        links.push({
          title: 'Negotiations',
          target: '/negotiations',
          badge: negotiationBadge > 0 ? negotiationBadge : undefined,
        });
      }
      links.push({ title: 'Settings', target: '/settings' });
      return links;
    }

    // Live mode (authenticated) - full navigation
    const links = [
      { title: 'Home', target: '/home' },
      { title: 'Cars', target: '/cars' },
    ];

    // Add Negotiations link with badge count
    if (hasProAccess) {
      links.push({
        title: 'Negotiations',
        target: '/negotiations',
        badge: negotiationBadge > 0 ? negotiationBadge : undefined,
      });
    }

    links.push({ title: 'Settings', target: '/profile' }, { title: 'Logout', onPress: logout });

    return links;
  }, [hasProAccess, confirmedRecommendationCount, negotiationBadge, logout, mode]);

  return navLinks;
};

export default useNavLinks;
